<template>
  <div class="min-h-screen bg-gray-50 py-4 pb-24">
    <div class="container mx-auto px-2 sm:px-4 w-full max-w-4xl">
      <!-- Header Section - Redesigned -->
      <div class="flex justify-between items-center mb-6">
        <router-link 
          to="/in-progress" 
          class="text-gray-600 hover:text-gray-800 transition duration-200 flex items-center gap-1"
        >
          <span class="material-icons text-xl">arrow_back</span>
          <span class="text-sm">Volver</span>
        </router-link>
        
        <div class="flex items-center">
          <!-- Display status pill -->
          <div 
            class="rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1 mr-2"
            :class="{
              'bg-green-100 text-green-800': checklist?.status === 'completed',
              'bg-blue-100 text-blue-800': checklist?.status === 'in_progress' || !checklist?.status
            }"
          >
            <span class="material-icons text-xs">{{ getStatusIcon(checklist?.status) }}</span>
            <span>{{ formatStatus(checklist?.status || 'in_progress') }}</span>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div v-if="checklist" class="bg-white rounded-lg shadow-lg p-3 sm:p-6 overflow-hidden">
        <div v-if="errorMessage" class="mt-4 p-4 bg-red-100 text-red-700 rounded">
          {{ errorMessage }}
        </div>

        <!-- Checklist Number and Title - Enhanced styling -->
        <div class="text-center mb-8">
          <h1 class="text-3xl sm:text-4xl font-bold text-blue-800">
            {{ displayNumber }}
          </h1>
          <h2 class="text-xl text-gray-600 mt-2">
            {{ checklist?.title || checklist?.originalTitle }}
          </h2>
        </div>

        <!-- Client Information Section -->
        <div class="mb-6">
          <!-- Info Summary Panel -->
          <div class="bg-gray-50 rounded-lg p-4 relative">
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div class="text-sm text-gray-500 font-medium">Cliente</div>
                <div class="text-gray-900">{{ clientName }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500 font-medium">Equipo</div>
                <div class="text-gray-900">{{ equipment }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500 font-medium">Período</div>
                <div class="text-gray-900">{{ period }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500 font-medium">Segundo Técnico</div>
                <div class="text-gray-900">
                  {{ getSecondTechnicianName }}
                </div>
              </div>
            </div>
            
            <!-- Edit Button -->
            <button
              @click="showEditMetadata = true"
              class="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
              title="Editar Información"
            >
              <span class="material-icons text-lg">edit</span>
            </button>
          </div>

          <!-- Edit Modal -->
          <EditMetadataModal
            :showEditMetadata="showEditMetadata"
            :editForm="editForm"
            :availableTechnicians="availableTechnicians"
            @save-metadata-changes="saveMetadataChanges"
            @update:showEditMetadata="showEditMetadata = $event"
          />
        </div>

        <!-- Checklist Items -->
        <div class="space-y-6">
          <!-- Section-based format -->
          <template v-if="checklist.sections && checklist.sections.length">
            <SectionComponent
              v-for="section in checklist.sections"
              :key="section.id"
              :section="section"
              @update-item="updateItem"
            />
          </template>
          
          <!-- Legacy format (direct items) -->
          <template v-else-if="checklist.items && checklist.items.length">
            <div class="space-y-4">
              <ChecklistItem
                v-for="item in checklist.items"
                :key="item.id"
                :item="item"
                :sectionTitle="checklist.title || checklist.originalTitle"
                @update-item="updateItem"
              />
            </div>
          </template>
          
          <template v-else>
            <div class="text-center py-8 text-gray-500">
              No items found in this checklist.
            </div>
          </template>
        </div>

        <!-- General Observations/Comments Section -->
        <div class="mt-10 mb-6">
          <div class="bg-gray-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold mb-3">Observaciones Generales</h3>
            <textarea
              v-model="generalComments"
              placeholder="Escriba sus observaciones generales aquí..."
              class="w-full p-4 border-2 rounded-lg bg-white placeholder-gray-400
                     focus:ring-2 focus:ring-blue-300 focus:border-blue-500 outline-none"
              rows="4"
            ></textarea>
          </div>
        </div>

        <!-- Signatures Section -->
        <div class="mt-8 space-y-4" id="signature-section">
          <!-- Client Signature -->
          <div class="p-2 sm:p-4 rounded"
               :class="{'bg-red-50 border border-red-300': !clientSigned}">
            <div class="flex justify-between items-center">
              <h3 class="text-lg font-semibold mb-2">Firma del Cliente</h3>
              <div class="w-2 h-2 rounded-full"
                   :class="clientSigned ? 'bg-green-500' : 'bg-red-500'"></div>
            </div>
            <canvas
              ref="clientCanvas"
              class="border rounded w-full touch-none max-w-full"
              :class="{'border-red-300': !clientSigned}"
              :width="windowWidth < 640 ? windowWidth - 32 : 400"
              :height="150"
              @mousedown="startDrawing"
              @mousemove="draw"
              @mouseup="stopDrawing"
              @mouseleave="stopDrawing"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="stopDrawing"
            ></canvas>
            <button 
              @click="() => clearSignature('client')"
              class="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 w-full sm:w-auto"
            >
              Clear
            </button>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-8 pb-10">
          <div class="flex flex-col sm:flex-row gap-4">
            <button 
              @click="generatePDF"
              :disabled="!isChecklistComplete || isGeneratingPDF"
              class="px-6 py-2 text-white rounded-lg flex items-center gap-2 justify-center relative overflow-hidden"
              :class="{
                'bg-green-500 hover:bg-green-600': isChecklistComplete && !isGeneratingPDF,
                'bg-gray-400 cursor-not-allowed': !isChecklistComplete || isGeneratingPDF
              }"
            >
              <template v-if="isGeneratingPDF">
                <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generando PDF...
              </template>
              <template v-else>
                <span class="material-icons">picture_as_pdf</span>
                <span v-if="isChecklistComplete">Completar y Generar PDF</span>
                <span v-else>Completar todos los items y firmar</span>
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Auto-save status indicator -->
    <div 
      v-if="saveStatus !== 'idle'" 
      class="fixed bottom-6 right-6 bg-blue-500 text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center z-50"
      :class="{
        'bg-green-500': saveStatus === 'success',
        'bg-blue-500': saveStatus === 'saving',
        'px-4 py-2': !isMobile,
        'w-12 h-12': isMobile
      }"
    >
      <!-- Loading Spinner -->
      <svg v-if="saveStatus === 'saving'"
           class="animate-spin-slow"
           :class="{ 'w-5 h-5': !isMobile, 'w-6 h-6': isMobile }"
           viewBox="0 0 24 24"
           xmlns="http://www.w3.org/2000/svg"
      >
        <circle 
          class="opacity-25" 
          cx="12" 
          cy="12" 
          r="10" 
          stroke="currentColor" 
          stroke-width="4" 
          fill="none"
        />
        <path 
          class="opacity-75" 
          fill="currentColor" 
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      
      <!-- Success Check -->
      <span v-else-if="saveStatus === 'success'"
            class="material-icons transform scale-110 animate-success-check">
        check
      </span>
      
      <!-- Status Text (desktop only) -->
      <span v-if="!isMobile" class="ml-2 text-sm">
        {{ saveStatus === 'saving' ? 'Saving...' : 'Saved!' }}
      </span>
    </div>

    <!-- Toast Messages -->
    <div v-if="successMessage" 
         class="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300 z-50"
         :class="{ 'opacity-0 translate-y-full': !successMessage }"
    >
      <span class="material-icons">check_circle</span>
      {{ successMessage }}
    </div>

    <div v-if="errorMessage" 
         class="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300 z-50"
         :class="{ 'opacity-0 translate-y-full': !errorMessage }"
    >
      <span class="material-icons">error</span>
      {{ errorMessage }}
    </div>

    <!-- Success Modal -->
    <SuccessModal
      v-if="showSuccessModal"
      title="¡Checklist Completado!"
      message="Su checklist ha sido generado y guardado exitosamente."
      @continue="goToChecklists"
    />
  </div>

  <!-- Mobile Jump to Section Menu -->
  <div v-if="isMobile && showJumpMenu" 
       class="fixed inset-0 bg-black bg-opacity-50 z-[60]"
       @click="showJumpMenu = false">
    <div class="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl p-4 max-h-[80vh] overflow-y-auto"
         @click.stop>
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">Secciones</h3>
        <button @click="showJumpMenu = false" class="p-2">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="space-y-2">
        <button
          v-for="section in checklist?.sections"
          :key="section.id"
          class="w-full text-left p-3 rounded-lg hover:bg-gray-100 transition-colors"
          :class="{'bg-blue-50 text-blue-700': currentSection?.id === section.id}"
          @click="jumpToSection(section.id)"
        >
          {{ section.title }}
        </button>
      </div>
    </div>
  </div>

  <!-- Bottom Fixed Completion Bar -->
  <div v-if="checklist" 
       class="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
    <div class="container mx-auto px-4 py-2">
      <!-- Progress Bar -->
      <div class="h-1 w-full bg-gray-200 rounded-full mb-2">
        <div class="h-full rounded-full transition-all duration-300"
             :class="{
               'bg-blue-500': completionPercentage < 100,
               'bg-green-500': completionPercentage === 100
             }"
             :style="`width: ${completionPercentage}%`"></div>
      </div>
      
      <div class="flex justify-between items-center">
        <!-- Left side - Progress info -->
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium" :class="{'text-green-600': completionPercentage === 100}">
            {{ completionPercentage }}%
          </span>
          <span class="text-xs text-gray-500">
            ({{ completedItems }}/{{ totalItems }} items)
          </span>
        </div>

        <!-- Center - Sign Now button when 100% complete and signature section not visible -->
        <div v-if="completionPercentage === 100 && !clientSigned && !isSignatureSectionVisible" 
             class="absolute left-1/2 transform -translate-x-1/2 -translate-y-16">
          <button
            @click="scrollToSignature"
            class="px-4 py-2 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 
                   flex items-center gap-2 animate-bounce-gentle"
          >
            <span class="material-icons text-sm">draw</span>
            Firmar
          </button>
        </div>

        <!-- Right side - Current section & jump (mobile only) -->
        <div class="flex items-center gap-2">
          <div v-if="isMobile" class="flex items-center">
            <button 
              @click="showJumpMenu = true"
              class="flex items-center gap-1 text-sm text-blue-600 font-medium"
            >
              <span class="truncate max-w-[150px]">
                {{ currentSection?.title }}
              </span>
              <span class="material-icons text-sm">expand_less</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick, onUnmounted, watch } from 'vue';
import { useStore } from '../store/store';
import { useRoute, useRouter } from 'vue-router';
import { useAuth } from '../firebase/auth'; // Add this import
import { PDFGenerator } from '../services/pdfGenerator';
import SuccessModal from '../components/viewer/SuccessModal.vue';
import EditMetadataModal from '../components/viewer/EditMetadataModal.vue';
import SectionComponent from '../components/viewer/SectionComponent.vue'; // Import the new component
import ChecklistItem from '../components/viewer/ChecklistItem.vue'; // Import ChecklistItem component

// Add debounce utility
const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

export default {
  components: {
    SuccessModal,
    EditMetadataModal,
    SectionComponent, // Add the new component
    ChecklistItem, // Register ChecklistItem component
  },
  setup() {
    const store = useStore();
    const { user } = useAuth(); // Add this line
    const route = useRoute();
    const router = useRouter(); // Initialize router
    const checklist = ref(null);
    const errorMessage = ref('');
    const successMessage = ref('');
    const inspectorCanvas = ref(null);
    const clientCanvas = ref(null);
    const isDrawing = ref(false);
    const currentContext = ref(null);
    const currentCanvas = ref(null);
    const lastPos = ref({ x: 0, y: 0 });
    const inspectorSigned = ref(false);
    const clientSigned = ref(false);
    const clientName = ref('');
    const showClientNameError = ref(false);
    const showFloatingButtons = ref(false);
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 640);
    const equipment = ref('');
    const period = ref('');
    const showValidationErrors = ref(false);
    const showStatusMenu = ref(false);
    const isGeneratingPDF = ref(false);
    const secondTechnicianId = ref('');
    const users = ref([]);
    const showSuccessModal = ref(false);
    const generalComments = ref(''); // Add new ref for general comments
    const isSignatureSectionVisible = ref(false);

    const availableTechnicians = computed(() => {
      return users.value.filter(u => u.id !== user.value?.uid);
    });

    const remainingItems = computed(() => {
      if (!checklist.value) return 0;
      
      if (checklist.value.sections?.length > 0) {
        return checklist.value.sections.reduce((total, section) =>
          total + section.subsections.reduce((subTotal, subsection) =>
            subTotal + subsection.items.filter(item => !item.status).length
          , 0)
        , 0);
      }
      
      return checklist.value.items.filter(item => !item.status).length;
    });

    const allItemsComplete = computed(() => {
      if (!checklist.value) return false;
      
      if (checklist.value.sections?.length > 0) {
        // Check section items
        for (const section of checklist.value.sections) {
          // Check direct section items
          if (section.items?.length) {
            if (section.items.some(item => !item.status)) {
              return false;
            }
          }
          
          // Check subsection items
          for (const subsection of section.subsections) {
            if (subsection.items.some(item => !item.status)) {
              return false;
            }
          }
        }
        return true;
      }
      
      // Old format (no sections)
      return checklist.value.items?.every(item => item.status) || false;
    });

    // Keep the isChecklistComplete calculation the same
    const isChecklistComplete = computed(() => {
      return allItemsComplete.value && clientSigned.value;
    });

    const hasSignature = (canvas) => {
      if (!canvas) return false;
      const ctx = canvas.getContext('2d');
      try {
        const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let nonEmptyPixels = 0;
        
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] > 20) {
            nonEmptyPixels++;
          }
        }
        
        return nonEmptyPixels > 100;
      } catch (error) {
        console.error('Error checking signature:', error);
        return false;
      }
    };

    const updateSignatureStatus = () => {
      if (inspectorCanvas.value) {
        inspectorSigned.value = hasSignature(inspectorCanvas.value);
      }
      if (clientCanvas.value) {
        clientSigned.value = hasSignature(clientCanvas.value);
      }
    };

    const initSignature = async (canvas, signatureData) => {
      if (!canvas || !signatureData) return false;
      
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          nextTick(() => {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              resolve(hasSignature(canvas));
            } else {
              resolve(false);
            }
          });
        };
        img.onerror = () => resolve(false);
        img.src = signatureData;
      });
    };

    const getCanvasCoordinates = (event, canvas) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      if (event.type.startsWith('touch')) {
        return {
          x: (event.touches[0].clientX - rect.left) * scaleX,
          y: (event.touches[0].clientY - rect.top) * scaleY
        };
      }
      
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY
      };
    };

    const startDrawing = (event) => {
      isDrawing.value = true;
      currentCanvas.value = event.target;
      const ctx = currentCanvas.value.getContext('2d');
      ctx.lineWidth = 1.5; // Standardize line width
      ctx.strokeStyle = '#444444'; // Lighter black
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      currentContext.value = ctx;
      const coords = getCanvasCoordinates(event, currentCanvas.value);
      lastPos.value = coords;
    };

    const draw = (event) => {
      if (!isDrawing.value) return;
      
      event.preventDefault();
      const canvas = event.target;
      const coords = getCanvasCoordinates(event, canvas);
      
      currentContext.value.beginPath();
      currentContext.value.moveTo(lastPos.value.x, lastPos.value.y);
      currentContext.value.lineTo(coords.x, coords.y);
      currentContext.value.stroke();
      
      lastPos.value = coords;
    };

    const handleTouchStart = (event) => {
      event.preventDefault();
      startDrawing(event);
    };

    const handleTouchMove = (event) => {
      event.preventDefault();
      draw(event);
    };

    const stopDrawing = () => {
      if (!isDrawing.value) return;
      isDrawing.value = false;
      updateSignatureStatus();
      
      // Trigger save when signature changes
      if (clientCanvas.value && hasSignature(clientCanvas.value)) {
        needsSave.value = true;
      }
      
      currentCanvas.value = null;
      currentContext.value = null;
    };

    const clearSignature = (type) => {
      const canvas = type === 'inspector' ? inspectorCanvas.value : clientCanvas.value;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        updateSignatureStatus();
        
        // Trigger save when signature is cleared
        if (type === 'client') {
          needsSave.value = true;
        }
      }
    };

    const generatePDF = async () => {
      // Additional check to prevent PDF generation if not all conditions are met
      if (isGeneratingPDF.value) return;
      
      // Double-check completion status before proceeding
      if (!allItemsComplete.value) {
        errorMessage.value = 'Debe completar todos los elementos del checklist primero.';
        return;
      }
      
      if (!clientSigned.value) {
        errorMessage.value = 'Se requiere la firma del cliente.';
        return;
      }
      
      try {
        isGeneratingPDF.value = true;
        
        // First save progress to ensure we have valid metadata
        const savedChecklist = await saveProgress();
        if (!savedChecklist) {
          errorMessage.value = 'Failed to save checklist';
          return;
        }
        
        // Update local checklist with saved data
        checklist.value = {
          ...checklist.value,
          ...savedChecklist
        };

        const footerSettings = await store.loadSettings();
        
        // Get the saved inspector signature
        const inspectorSignature = await store.getInspectorSignature();
        if (!inspectorSignature) {
          errorMessage.value = 'Please set up your signature in Settings first';
          return;
        }

        // Get the second technician name from our computed property
        const secondTechName = getSecondTechnicianName.value;

        const pdfGenerator = new PDFGenerator(
          checklist.value,
          clientName.value,
          equipment.value,
          period.value,
          clientCanvas.value,
          footerSettings,
          checklist.value.userName,
          secondTechName // Pass the second technician name
        );

        // Set the inspector signature
        pdfGenerator.setInspectorSignature(inspectorSignature);

        const pdfBlob = await pdfGenerator.generate();

        // Upload the PDF with verified metadata
        await store.uploadPDF(pdfBlob, {
          checklistId: checklist.value.id,
          checklistTitle: checklist.value.title,
          clientName: clientName.value,
          combinedNumber: checklist.value.combinedNumber
        });

        // After successful PDF generation, mark as completed
        if (checklist.value.status !== 'completed') {
          await updateStatus('completed');
        }

        // Show success modal instead of downloading
        showSuccessModal.value = true;

      } catch (error) {
        console.error('Error generating PDF:', error);
        errorMessage.value = 'Failed to generate PDF';
      } finally {
        isGeneratingPDF.value = false;
      }
    };

    const goToChecklists = () => {
      showSuccessModal.value = false;
      router.push({ 
        path: '/in-progress',
        // Add this query parameter to indicate we want to scroll to top
        query: { scrollToTop: 'true' } 
      });
    };

    const getRadioOptions = (item) => {
      return item.options.filter(opt => opt.type === 'radio' || !opt.type);
    };

    const getInputOptions = (item) => {
      return item.options.filter(opt => opt.type === 'input');
    };



    const validateForm = () => {
      if (!clientName.value || !equipment.value || !period.value || !secondTechnicianId.value) {
        showValidationErrors.value = true;
        return false;
      }
      showValidationErrors.value = false;
      return true;
    };

    const saveStatus = ref('idle'); // 'idle' | 'saving' | 'success'

    const saveProgress = async () => {
      try {
        if (!validateForm()) {
          errorMessage.value = 'Please fill in all required fields';
          return;
        }

        showClientNameError.value = false;
        errorMessage.value = '';

        const isCompleted = route.params.type === 'completed';
        
        // Create deep copy of sections and items to preserve all data
        const sections = checklist.value.sections?.map(section => ({
          ...section,
          subsections: section.subsections.map(subsection => ({
            ...subsection,
            items: subsection.items.map(item => ({
              ...item,
              status: item.status || '',
              comments: item.comments || '',
              options: item.options.map(opt => ({
                ...opt,
                value: opt.value || ''
              }))
            }))
          }))
        }));

        const items = (checklist.value.items || []).map(item => ({
          ...item,
          status: item.status || '',
          comments: item.comments || '',
          options: item.options.map(opt => ({
            ...opt,
            value: opt.value || ''
          }))
        }));

        const savedData = {
          id: isCompleted ? checklist.value?.id : undefined,
          templateId: isCompleted ? checklist.value.templateId || checklist.value.template_id : route.params.id,
          template_id: isCompleted ? checklist.value.templateId || checklist.value.template_id : route.params.id,
          title: checklist.value.originalTitle || checklist.value.title,
          originalTitle: checklist.value.originalTitle || checklist.value.title,
          clientName: clientName.value,
          equipment: equipment.value,
          period: period.value,
          sections, // Use our deep copied sections
          items,    // Use our deep copied items
          inspectorSignature: inspectorCanvas.value?.toDataURL() || null,
          clientSignature: clientCanvas.value?.toDataURL() || null,
          generalComments: generalComments.value, // Add general comments to saved data
          rev: checklist.value.rev || 1,
          number: checklist.value.number,
          combinedNumber: isCompleted ? checklist.value.combinedNumber : undefined,
          completionNumber: isCompleted ? checklist.value.completionNumber : undefined,
          templateNumber: isCompleted ? checklist.value.templateNumber : undefined,
          secondTechnicianId: secondTechnicianId.value,
        };

        const result = await store.saveCompletedChecklist(savedData);
        
        // Update the local checklist with the saved data
        checklist.value = {
          ...checklist.value,
          ...result
        };

        // Only replace route if not completed
        if (result && !isCompleted) {
          router.replace({
            name: 'Viewer',
            params: { id: result.id, type: 'completed' }
          });
        }

        return result;
      } catch (error) {
        console.error('Error saving progress:', error);
        errorMessage.value = 'Failed to save progress';
        throw error;
      }
    };

    const lastSignatureData = ref(null);  // Add this to store signature data

    // Modify handleResize to preserve signature
    const handleResize = () => {
      if (clientCanvas.value) {
        // Store current signature before resize
        lastSignatureData.value = clientCanvas.value.toDataURL();
        
        // Update window width
        windowWidth.value = window.innerWidth;
        
        // Wait for next tick to ensure canvas size is updated
        nextTick(() => {
          if (lastSignatureData.value) {
            const img = new Image();
            img.onload = () => {
              const ctx = clientCanvas.value.getContext('2d');
              ctx.clearRect(0, 0, clientCanvas.value.width, clientCanvas.value.height);
              ctx.drawImage(img, 0, 0, clientCanvas.value.width, clientCanvas.value.height);
              updateSignatureStatus();
            };
            img.src = lastSignatureData.value;
          }
        });
      }
    };

    // Add resize event listener with debounce
    onMounted(() => {
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleResize, 100);
      });
    });

    const getStatusIcon = (status) => {
      switch (status) {
        case 'completed':
          return 'check_circle';
        case 'in_progress':
          return 'pending';
        default:
          return 'radio_button_unchecked';
      }
    };

    const formatStatus = (status) => {
      switch (status) {
        case 'in_progress':
          return 'In Progress';
        case 'completed':
          return 'Completed';
        default:
          return 'Unknown';
      }
    };

    const updateStatus = async (newStatus) => {
      try {
        await store.updateChecklistStatus(checklist.value.id, newStatus);
        checklist.value.status = newStatus;
        showStatusMenu.value = false;
        successMessage.value = `Status updated to ${formatStatus(newStatus)}`;
        setTimeout(() => successMessage.value = '', 3000);
      } catch (error) {
        errorMessage.value = 'Failed to update status';
      }
    };

    const saveInspectorSignature = async () => {
      try {
        if (!inspectorCanvas.value) return;
        const signatureData = inspectorCanvas.value.toDataURL();
        await store.saveInspectorSignature(signatureData);
        successMessage.value = 'Signature saved as default!';
        setTimeout(() => successMessage.value = '', 3000);
      } catch (error) {
        errorMessage.value = 'Failed to save signature';
      }
    };

    const showEditMetadata = ref(false);
    const editForm = ref({
      clientName: '',
      equipment: '',
      period: '',
      secondTechnicianId: ''
    });

    const getSecondTechnicianName = computed(() => {
      if (secondTechnicianId.value === 'no_aplica') return 'No Aplica';
      const tech = availableTechnicians.value.find(t => t.id === secondTechnicianId.value);
      return tech ? tech.name : 'Unknown';
    });

    const saveMetadataChanges = async (updatedForm) => {
      clientName.value = updatedForm.clientName;
      equipment.value = updatedForm.equipment;
      period.value = updatedForm.period;
      secondTechnicianId.value = updatedForm.secondTechnicianId;
      
      await saveProgress();
      showEditMetadata.value = false;
    };

    const displayNumber = computed(() => {
      const number = checklist.value?.combinedNumber || 
                    checklist.value?.number || 
                    'No Number';
      return number;
    });

    const needsSave = ref(false);
    const autoSaveTimer = ref(null);
    const isAutoSaveEnabled = computed(() => 
      checklist.value?.combinedNumber || checklist.value?.number
    );

    // Function to handle auto-saving
    const startAutoSave = () => {
      if (autoSaveTimer.value) {
        clearInterval(autoSaveTimer.value);
      }

      const debouncedSave = debounce(async () => {
        if (!needsSave.value || !isAutoSaveEnabled.value) return;
        
        try {
          saveStatus.value = 'saving';
          await saveProgress();
          needsSave.value = false;
          saveStatus.value = 'success';
          
          setTimeout(() => {
            if (saveStatus.value === 'success') {
              saveStatus.value = 'idle';
            }
          }, 1000);
        } catch (error) {
          console.error('Auto-save failed:', error);
          saveStatus.value = 'idle';
        }
      }, 2000); // Increased debounce to 2 seconds

      // Watch for changes that should trigger a save
      watch([
        () => checklist.value?.sections,
        () => checklist.value?.items,
        () => clientName.value,
        () => equipment.value,
        () => period.value,
        () => secondTechnicianId.value,
        () => generalComments.value, // Add general comments to the watch
      ], () => {
        needsSave.value = true;
        debouncedSave();
      }, { deep: true });

      // Watch signature changes separately
      watch(() => clientSigned.value, (newValue, oldValue) => {
        if (newValue !== oldValue) {
          needsSave.value = true;
          debouncedSave();
        }
      });
    };

    // Initialize watchers when component is mounted
    onMounted(async () => {
      try {
        window.addEventListener('resize', handleResize);
        
        await Promise.all([
          store.loadSettings(),
          store.loadChecklists(),
          store.loadUsers(),
          store.loadCompletedChecklists()
        ]);

        const id = route.params.id;
        const type = route.params.type || 'completed'; // Changed default to 'completed'

        if (type === 'completed') {
          const loadedChecklist = store.getCompletedChecklistById(id);
          
          if (loadedChecklist) {
            // Load metadata
            clientName.value = loadedChecklist.clientName;
            equipment.value = loadedChecklist.equipment || '';
            period.value = loadedChecklist.period || '';
            secondTechnicianId.value = loadedChecklist.secondTechnicianId || '';
            generalComments.value = loadedChecklist.generalComments || ''; // Load general comments
            
            // Ensure sections and items are properly loaded
            checklist.value = {
              ...loadedChecklist,
              sections: loadedChecklist.sections || [],
              items: loadedChecklist.items || []
            };

            // Initialize signatures
            await nextTick();
            if (loadedChecklist.inspectorSignature) {
              inspectorSigned.value = await initSignature(
                inspectorCanvas.value,
                loadedChecklist.inspectorSignature
              );
            }

            if (loadedChecklist.clientSignature) {
              clientSigned.value = await initSignature(
                clientCanvas.value,
                loadedChecklist.clientSignature
              );
            }

            editForm.value = {
              clientName: loadedChecklist.clientName || '',
              equipment: loadedChecklist.equipment || '',
              period: loadedChecklist.period || '',
              secondTechnicianId: loadedChecklist.secondTechnicianId || ''
            };
          }
        }

        // Load saved signature and users
        await store.loadUsers();
        users.value = store.users;
      } catch (error) {
        console.error('Error in mounted:', error);
        errorMessage.value = 'Failed to load checklist data';
      }

      // Start auto-save if enabled (no need to call watchForChanges)
      if (isAutoSaveEnabled.value) {
        startAutoSave();
      }
    });

    onUnmounted(() => {
      // Remove resize listener
      window.removeEventListener('resize', handleResize);
      // Remove scroll event listener cleanup
    });

    const isMobile = computed(() => {
      return windowWidth.value < 640;
    });

    const showJumpMenu = ref(false);
    const currentSection = ref(null);
    
    // Computed properties for completion tracking
    const totalItems = computed(() => {
      if (!checklist.value) return 0;
      
      // If checklist has sections
      if (checklist.value.sections?.length) {
        return checklist.value.sections.reduce((total, section) => {
          const sectionItems = (section.items || []).length;
          const subsectionItems = section.subsections.reduce((subTotal, subsection) => {
            return subTotal + (subsection.items || []).length;
          }, 0);
          return total + sectionItems + subsectionItems;
        }, 0);
      }
      
      // If checklist has only items
      return (checklist.value.items || []).length;
    });

    const completedItems = computed(() => {
      if (!checklist.value) return 0;
      
      // If checklist has sections
      if (checklist.value.sections?.length) {
        return checklist.value.sections.reduce((total, section) => {
          const sectionCompleted = (section.items || []).filter(item => item.status).length;
          const subsectionCompleted = section.subsections.reduce((subTotal, subsection) => {
            return subTotal + (subsection.items || []).filter(item => item.status).length;
          }, 0);
          return total + sectionCompleted + subsectionCompleted;
        }, 0);
      }
      
      // If checklist has only items
      return (checklist.value.items || []).filter(item => item.status).length;
    });

    const completionPercentage = computed(() => {
      if (totalItems.value === 0) return 0;
      return Math.round((completedItems.value / totalItems.value) * 100);
    });

    // Intersection Observer for section tracking
    const setupSectionObserver = () => {
      const sectionStates = new Map();
      
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            const rect = entry.boundingClientRect;
            const sectionId = entry.target.getAttribute('data-section-id');
            
            sectionStates.set(sectionId, {
              id: sectionId,
              isIntersecting: entry.isIntersecting,
              top: rect.top,
              bottom: rect.bottom
            });
          });

          const visibleSections = Array.from(sectionStates.values())
            .filter(section => section.isIntersecting || section.bottom <= 0)
            .sort((a, b) => {
              if (a.isIntersecting && b.isIntersecting) {
                return Math.abs(a.top) - Math.abs(b.top);
              }
              if (a.isIntersecting) return -1;
              if (b.isIntersecting) return 1;
              return b.bottom - a.bottom;
            });

          if (visibleSections.length > 0) {
            const topSection = visibleSections[0];
            const section = checklist.value?.sections.find(s => s.id === topSection.id);
            
            if (section && currentSection.value?.id !== section.id) {
              currentSection.value = section;
            }
          }
        },
        {
          threshold: [0],
          rootMargin: '-20% 0px -50% 0px'
        }
      );

      return observer;
    };

    // Add new method to scroll to signature
    const scrollToSignature = () => {
      const signatureSection = document.querySelector('#signature-section');
      if (signatureSection) {
        signatureSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
        // Add a highlight effect
        signatureSection.classList.add('highlight-signature');
        setTimeout(() => {
          signatureSection.classList.remove('highlight-signature');
        }, 2000);
      }
    };

    // Modified initialization function
    const initSectionTracking = () => {
      if (!checklist.value?.sections?.length) {
        return;
      }

      // Only set initial section if none is selected
      if (!currentSection.value) {
        currentSection.value = checklist.value.sections[0];
      }

      nextTick(() => {
        if (window._sectionObserver) {
          window._sectionObserver.disconnect();
        }

        const observer = setupSectionObserver();
        window._sectionObserver = observer;

        const sections = document.querySelectorAll('[data-section-id]');
        sections.forEach(section => observer.observe(section));
      });
    };

    // Modified watch setup
    watch(
      () => checklist.value,
      (newChecklist, oldChecklist) => {
        // Only initialize tracking if this is the first load or sections have actually changed
        if (newChecklist?.sections?.length && 
            (!oldChecklist || 
             !oldChecklist.sections ||
             newChecklist.sections.length !== oldChecklist.sections.length ||
             newChecklist.sections.some((section, idx) => section.id !== oldChecklist.sections[idx]?.id)
            )) {
          // Only reset current section if it's not already set
          if (!currentSection.value) {
            currentSection.value = newChecklist.sections[0];
          }
          setTimeout(initSectionTracking, 100);
        }
      },
      { immediate: true }
    );

    onUnmounted(() => {
      if (window._sectionObserver) {
        window._sectionObserver.disconnect();
        window._sectionObserver = null;
      }
    });

    const jumpToSection = (sectionId) => {
      const element = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        showJumpMenu.value = false;
      }
    };

    // Simplified setupSignatureObserver without debug logs
    const setupSignatureObserver = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            isSignatureSectionVisible.value = entry.isIntersecting;
          });
        },
        {
          threshold: [0, 0.1, 0.5, 1],
          rootMargin: '-100px 0px'
        }
      );

      return observer;
    };

    // Simplified setupSignatureObserverWithRetry without debug logs
    const setupSignatureObserverWithRetry = () => {
      const signatureSection = document.querySelector('#signature-section');
      if (signatureSection) {
        const observer = setupSignatureObserver();
        observer.observe(signatureSection);
        window._signatureObserver = observer;
      } else {
        setTimeout(setupSignatureObserverWithRetry, 500);
      }
    };

    // Modify onMounted to ensure proper observer setup
    onMounted(() => {

      // Setup signature section observer with retry
      nextTick(() => {
        setupSignatureObserverWithRetry();
      });
    });

    // Add cleanup for signature observer
    onUnmounted(() => {
      // ...existing unmounted code...
      if (window._signatureObserver) {
        window._signatureObserver.disconnect();
        window._signatureObserver = null;
      }
    });

    const updateItem = (updatedItem) => {
      // First check if it's a direct/legacy item
      if (checklist.value?.items && checklist.value.items.length) {
        const itemIndex = checklist.value.items.findIndex(item => item.id === updatedItem.id);
        if (itemIndex !== -1) {
          checklist.value.items[itemIndex] = updatedItem;
          needsSave.value = true;
          return; // Exit early if we found and updated the item
        }
      }
      
      // If not in direct items, check in sections and subsections
      if (checklist.value?.sections) {
        const section = checklist.value.sections.find(section => {
          // Check if section has items safely
          const hasItemsInSection = section.items && 
            section.items.some(item => item.id === updatedItem.id);
          
          // Check if section has subsections with items safely
          const hasItemsInSubsection = section.subsections && 
            section.subsections.some(subsection => 
              subsection.items && subsection.items.some(item => item.id === updatedItem.id)
            );
          
          return hasItemsInSection || hasItemsInSubsection;
        });

        if (section) {
          // Handle item in section's direct items
          if (section.items && section.items.length) {
            const itemInSection = section.items.find(item => item.id === updatedItem.id);
            if (itemInSection) {
              Object.assign(itemInSection, updatedItem);
              needsSave.value = true;
              return;
            }
          }

          // Handle item in subsections
          if (section.subsections && section.subsections.length) {
            const subsection = section.subsections.find(subsection => 
              subsection.items && subsection.items.some(item => item.id === updatedItem.id)
            );
            
            if (subsection && subsection.items) {
              const itemIndex = subsection.items.findIndex(item => item.id === updatedItem.id);
              if (itemIndex !== -1) {
                subsection.items[itemIndex] = updatedItem;
                needsSave.value = true;
              }
            }
          }
        }
      }
    };

    return {
      checklist,
      errorMessage,
      successMessage,
      inspectorCanvas,
      clientCanvas,
      isDrawing,
      currentContext,
      currentCanvas,
      lastPos,
      inspectorSigned,
      clientSigned,
      remainingItems,
      allItemsComplete,
      isChecklistComplete,
      startDrawing,
      draw,
      handleTouchStart,
      handleTouchMove,
      stopDrawing,
      clearSignature,
      generatePDF,
      getRadioOptions,
      getInputOptions,
      saveProgress,
      clientName,
      showClientNameError,
      showFloatingButtons,
      windowWidth,
      equipment,
      period,
      showValidationErrors,
      showStatusMenu,
      getStatusIcon,
      formatStatus,
      updateStatus,
      saveInspectorSignature,
      isGeneratingPDF,
      secondTechnicianId,
      availableTechnicians,
      users,
      showSuccessModal,
      goToChecklists,
      showEditMetadata,
      editForm,
      saveMetadataChanges,
      getSecondTechnicianName,
      generalComments, // Expose general comments to the template
      displayNumber,
      saveStatus,
      needsSave,
      isAutoSaveEnabled,
      isMobile,
      lastSignatureData,
      showJumpMenu,
      currentSection,
      completionPercentage,
      completedItems,
      totalItems,
      jumpToSection,
      scrollToSignature,
      isSignatureSectionVisible,
      updateItem,
    };
  }
};
</script>

<style scoped>
.success-toast-enter-active,
.success-toast-leave-active {
  transition: all 0.3s ease;
}

.success-toast-enter-from,
.success-toast-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

/* Add smooth transitions for status changes */
.checklist-status-indicator {
  transition: all 0.3s ease;
}

@media (max-width: 640px) {
  .container {
    padding-bottom: 1.5rem;
  }
}

/* Add these new styles */
.container {
  min-width: 320px; /* Minimum width for smallest devices */
}

canvas {
  touch-action: none; /* Prevent scrolling while drawing on mobile */
}

/* Keep existing styles */
.status-menu-enter-active,
.status-menu-leave-active {
  transition: all 0.2s ease;
}

.status-menu-enter-from,
.status-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes success-check {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-success-check {
  animation: success-check 0.3s ease-out forwards;
}

@keyframes spin-slow {
  from { 
    transform: rotate(0deg); 
  }
  to { 
    transform: rotate(360deg); 
  }
}

.animate-spin-slow {
  animation: spin-slow 1.5s linear infinite;
  transform-origin: center center;
}

@keyframes bounce-gentle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

.animate-bounce-gentle {
  animation: bounce-gentle 2s infinite;
}

.highlight-signature {
  animation: highlight-pulse 2s ease-out;
}

@keyframes highlight-pulse {
  0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
}
</style>
